import React, { useState } from "react";
import { useNavigate } from "react-router";
import { useTranslation } from "react-i18next";
import PhoneInput from "../components/PhoneInput";
import Input from "../components/Input";
import LanguageSelector from "../components/LanguageSelector";
import { Link } from "react-router";
import { useContactMutation, useUploadContactImagesMutation } from "../redux/services/auth.service.ts";
import { useToast } from "../hooks/useToast";

const GetStarted = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { showSuccess, showError } = useToast();

  const [contactMutation, { isLoading: isContactLoading }] =
    useContactMutation();
  const [uploadImagesMutation, { isLoading: isUploadingImages }] =
    useUploadContactImagesMutation();

  // Temporary UI state for current question selection (resets when navigating)
  const [selectedOptions, setSelectedOptions] = useState([]);
  const [consultationMethod, setConsultationMethod] = useState("");

  // Persistent state for all question answers
  const [answers, setAnswers] = useState({
    treatment_for: null, // Question 1: "myself" | "someone else"
    preferred_timing: null, // Question 2: string
    communication_preference: [], // Question 3: string[]
    treatment_purpose: [], // Question 4: string[]
    consultation_method: null, // Question 5: "online" | "in_person"
  });

  const [currentQuestion, setCurrentQuestion] = useState(1);
  const totalQuestions = 6;
  const [formSubmitted, setFormSubmitted] = useState(false);
  const [contactId, setContactId] = useState(null);

  const [uploadedPhotos, setUploadedPhotos] = useState({
    front: null,
    left: null,
    right: null,
    top: null,
    bottom: null,
    smile: null,
  });

  // Form state for question 6
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phoneCountryCode: "+1",
    phoneNumber: "",
    agreedToTerms: false,
  });

  const questions = [
    {
      id: 1,
      question: t("getStarted.question1"),
      instruction: t("getStarted.question1Instruction"),
      multiSelect: false,
      options: [
        t("getStarted.question1Option1"),
        t("getStarted.question1Option2"),
      ],
    },
    {
      id: 2,
      question: t("getStarted.question2"),
      instruction: t("getStarted.question2Instruction"),
      multiSelect: false,
      options: [
        t("getStarted.question2Option1"),
        t("getStarted.question2Option2"),
        t("getStarted.question2Option3"),
        t("getStarted.question2Option4"),
      ],
    },
    {
      id: 3,
      question: t("getStarted.question3"),
      instruction: t("getStarted.question3Instruction"),
      multiSelect: true,
      options: [
        t("getStarted.question3Option1"),
        t("getStarted.question3Option2"),
        t("getStarted.question3Option3"),
        t("getStarted.question3Option4"),
      ],
    },
    {
      id: 4,
      question: t("getStarted.question4"),
      instruction: t("getStarted.question4Instruction"),
      multiSelect: true,
      options: [
        t("getStarted.question4Option1"),
        t("getStarted.question4Option2"),
        t("getStarted.question4Option3"),
        t("getStarted.question4Option4"),
        t("getStarted.question4Option5"),
      ],
    },
    {
      id: 5,
      question: t("getStarted.question5"),
      instruction: t("getStarted.question5Instruction"),
      multiSelect: false,
      isConsultation: true,
    },
    {
      id: 6,
      question: t("getStarted.question6"),
      isForm: true,
    },
  ];

  const currentQuestionData = questions[currentQuestion - 1];

  // Helper function to map question 1 option text to API value
  const mapTreatmentFor = (optionText) => {
    const myselfText = t("getStarted.question1Option1");
    return optionText === myselfText ? "myself" : "someone else";
  };

  // Helper function to map consultation method to API value
  const mapConsultationMethod = (method) => {
    return method === "online" ? "online" : "in_person";
  };

  // Load selected options from answers when navigating to a question
  React.useEffect(() => {
    if (currentQuestion === 1) {
      if (answers.treatment_for) {
        const myselfText = t("getStarted.question1Option1");
        const optionText =
          answers.treatment_for === "myself"
            ? myselfText
            : t("getStarted.question1Option2");
        setSelectedOptions([optionText]);
      } else {
        setSelectedOptions([]);
      }
    } else if (currentQuestion === 2) {
      if (answers.preferred_timing) {
        setSelectedOptions([answers.preferred_timing]);
      } else {
        setSelectedOptions([]);
      }
    } else if (currentQuestion === 3) {
      if (answers.communication_preference.length > 0) {
        setSelectedOptions([...answers.communication_preference]);
      } else {
        setSelectedOptions([]);
      }
    } else if (currentQuestion === 4) {
      if (answers.treatment_purpose.length > 0) {
        setSelectedOptions([...answers.treatment_purpose]);
      } else {
        setSelectedOptions([]);
      }
    } else if (currentQuestion === 5) {
      if (answers.consultation_method) {
        const method =
          answers.consultation_method === "online" ? "online" : "in-person";
        setConsultationMethod(method);
      } else {
        setConsultationMethod("");
      }
    }
  }, [currentQuestion, answers, t]);

  const handleOptionSelect = (option) => {
    if (currentQuestionData?.multiSelect) {
      // Multi-select: toggle option in array
      setSelectedOptions((prev) =>
        prev.includes(option)
          ? prev.filter((opt) => opt !== option)
          : [...prev, option]
      );
    } else {
      // Single-select: replace with single option
      setSelectedOptions([option]);
    }
  };

  const handleFormChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  // Format all collected data to match API structure
  const formatSubmissionData = () => {
    return {
      firstname: formData.firstName,
      lastname: formData.lastName,
      email: formData.email,
      phone: `${formData.phoneCountryCode}${formData.phoneNumber}`,
      treatment_for: answers.treatment_for,
      preferred_timing: answers.preferred_timing,
      communication_preference: answers.communication_preference,
      treatment_purpose: answers.treatment_purpose,
      consultation_method: answers.consultation_method,
    };
  };

  const handleContinue = async () => {
    // For question 6 (form), validate form fields and submit
    if (currentQuestion === 6) {
      if (
        !formData.firstName ||
        !formData.lastName ||
        !formData.email ||
        !formData.phoneNumber ||
        !formData.agreedToTerms
      ) {
        return;
      }

      // Format and log the submission data
      const submissionData = formatSubmissionData();
      await contactMutation(submissionData)
        .unwrap()
        .then((response) => {
          // Extract contact ID from response
          const id = response?.data?.id || response?.id;
          if (id) {
            setContactId(id);
          }
          setFormSubmitted(true);
          showSuccess("Your enquiry has been submitted successfully!");
        })
        .catch((err) => {
          console.error("Contact mutation error:", err);
          showError("Failed to submit your enquiry. Please try again.");
        });
    }

    // Save current question's answer before moving to next question
    if (currentQuestion === 1) {
      // Question 1: Map option text to API value
      if (selectedOptions.length > 0) {
        setAnswers((prev) => ({
          ...prev,
          treatment_for: mapTreatmentFor(selectedOptions[0]),
        }));
      } else {
        return; // Don't proceed without selection
      }
    } else if (currentQuestion === 2) {
      // Question 2: Store timing option
      if (selectedOptions.length > 0) {
        setAnswers((prev) => ({
          ...prev,
          preferred_timing: selectedOptions[0],
        }));
      } else {
        return;
      }
    } else if (currentQuestion === 3) {
      // Question 3: Store communication preferences array
      if (selectedOptions.length > 0) {
        setAnswers((prev) => ({
          ...prev,
          communication_preference: [...selectedOptions],
        }));
      } else {
        return;
      }
    } else if (currentQuestion === 4) {
      // Question 4: Store treatment purposes array
      if (selectedOptions.length > 0) {
        setAnswers((prev) => ({
          ...prev,
          treatment_purpose: [...selectedOptions],
        }));
      } else {
        return;
      }
    } else if (currentQuestion === 5) {
      // Question 5: Map consultation method to API value
      if (consultationMethod) {
        setAnswers((prev) => ({
          ...prev,
          consultation_method: mapConsultationMethod(consultationMethod),
        }));
      } else {
        return;
      }
    }

    // Navigate to next question
    if (currentQuestion < totalQuestions) {
      setCurrentQuestion(currentQuestion + 1);
      // Reset temporary UI state (will be restored from answers by useEffect if going back)
      if (currentQuestion < 5) {
        setSelectedOptions([]);
      } else if (currentQuestion === 5) {
        setConsultationMethod("");
      }
    }
  };

  const handleBack = () => {
    if (currentQuestion > 1) {
      setCurrentQuestion(currentQuestion - 1);
      // Selected options will be restored from answers by useEffect
    }
  };

  const handleConsultationSelect = (method) => {
    setConsultationMethod(method);
  };

  const isFormValid = () => {
    if (currentQuestion === 6) {
      return (
        formData.firstName &&
        formData.lastName &&
        formData.email &&
        formData.phoneNumber &&
        formData.agreedToTerms
      );
    }
    if (currentQuestion === 5) {
      return consultationMethod !== "";
    }
    return selectedOptions.length > 0;
  };

  const handleClose = () => {
    navigate("/");
  };

  const maskEmail = (email) => {
    if (!email) return "";
    const [name, domain] = email.split("@");
    if (name.length <= 3) return `${name[0]}***@${domain}`;
    return `${name.substring(0, 3)}***${name[name.length - 1]}@${domain}`;
  };

  const maskPhone = (phone) => {
    if (!phone) return "";
    if (phone.length <= 6) return phone;
    return `${phone.substring(0, 3)}***${phone.substring(phone.length - 2)}`;
  };

  const handlePhotoUpload = (type, file) => {
    setUploadedPhotos((prev) => ({
      ...prev,
      [type]: file,
    }));
  };

  const handlePhotoUploadSubmit = async () => {
    // Validate contactId exists
    if (!contactId) {
      showError("Contact information not found. Please submit the form again.");
      return;
    }

    // Collect all uploaded photos and filter out null values
    const photosArray = Object.values(uploadedPhotos).filter(
      (photo) => photo !== null
    );

    // Validate at least one image is selected
    if (photosArray.length === 0) {
      showError("Please upload at least one photo before submitting.");
      return;
    }

    // Create FormData with images array format: images[0], images[1], etc.
    const formData = new FormData();
    photosArray.forEach((photo, index) => {
      formData.append(`images[${index}]`, photo);
    });

    try {
      await uploadImagesMutation({
        contactId,
        formData,
      })
        .unwrap()
        .then(() => {
          showSuccess("Photos uploaded successfully!");
          navigate("/");
        })
        .catch((err) => {
          console.error("Image upload error:", err);
          showError("Failed to upload photos. Please try again.");
        });
    } catch (err) {
      console.error("Image upload error:", err);
      showError("Failed to upload photos. Please try again.");
    }
  };

  const photoExamples = {
    front: "/assets/bgima.webp",
    left: "/assets/bgima1.webp",
    right: "/assets/bgima2.webp",
    top: "/assets/bgima3.webp",
    bottom: "/assets/bgima4.webp",
    smile: "/assets/bgima5.webp",
  };

  // Show confirmation and photo upload screen after form submission
  if (formSubmitted) {
    return (
      <div className="min-h-screen flex flex-col">
        {/* Header */}
        <div className="w-full border-b border-[#0000000D] bg-white">
          <div className="w-full px-4 sm:px-6 lg:px-8 py-4 flex items-center justify-between">
            <div className="max-w-6xl mx-auto w-full flex items-center justify-between">
              <Link to="/">
                <div>
                  <img
                    src="/assets/getlogo.svg"
                    alt="Dent Finder"
                    className="h-8 w-auto"
                  />
                </div>
              </Link>
              <LanguageSelector arrowColor="black" />
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div
          className="flex-1 flex flex-col px-4 sm:px-6 lg:px-8 py-12 bg-cover bg-center bg-no-repeat relative bg-[#F8FBFC]"
          style={{
            backgroundImage: "url(/assets/Bg.webp)",
          }}
        >
          <div className="max-w-6xl mx-auto w-full relative z-10">
            {/* Confirmation Message */}
            <div className="bg-[#F0F2F5] rounded-xl p-6 mb-8">
              <p className="text-sm text-[#010103] leading-relaxed mb-4">
                {formData.firstName}
                {t("getStarted.confirmationMessage")}
                {formData.phoneCountryCode}
                <span className="font-semibold">
                  {maskPhone(formData.phoneNumber)}
                </span>
                {t("getStarted.confirmationMessage2")}
                <span className="font-semibold">
                  {maskEmail(formData.email)}
                </span>
                {t("getStarted.confirmationMessage3")}
                <span className="font-semibold">
                  {formData.phoneCountryCode}
                  {maskPhone(formData.phoneNumber)}
                </span>
                {t("getStarted.confirmationMessage4")}
              </p>
              {/* <p className="text-sm text-[010103]">
                {t("getStarted.incorrectContactInfo")}{" "}
                <a
                  href="/#"
                  className="text-[#007B83] underline hover:opacity-80"
                >
                  {t("getStarted.here")}
                </a>
                .
              </p> */}
            </div>

            {/* Upload Section */}
            <div className="mb-8 mx-auto" style={{ maxWidth: "766px" }}>
              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-3 text-center bg-gradient-to-r from-[#000087] to-[#000000] bg-clip-text text-transparent">
                {t("getStarted.uploadPicturesTitle")}
              </h2>

              <p className="text-base sm:text-lg text-[#5A5A59] text-center mb-8">
                {t("getStarted.uploadPicturesDescription")}
              </p>
            </div>

            {/* Photo Upload Grid */}
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4 mb-12">
              {[
                { type: "front", label: t("getStarted.photoFront") },
                { type: "left", label: t("getStarted.photoLeft") },
                { type: "right", label: t("getStarted.photoRight") },
                { type: "top", label: t("getStarted.photoTop") },
                { type: "bottom", label: t("getStarted.photoBottom") },
                { type: "smile", label: t("getStarted.photoSmile") },
              ].map(({ type, label }) => (
                <div key={type} className="relative">
                  <label className="cursor-pointer block">
                    <input
                      type="file"
                      accept="image/*"
                      onChange={(e) => {
                        if (e.target.files[0]) {
                          handlePhotoUpload(type, e.target.files[0]);
                        }
                      }}
                      className="hidden"
                    />
                    <div
                      className="aspect-square rounded-xl  bg-white flex items-center justify-center overflow-hidden hover:border-[#14C5D1] transition-colors bg-cover bg-center bg-no-repeat"
                      style={{
                        backgroundImage: uploadedPhotos[type]
                          ? `url(${URL.createObjectURL(uploadedPhotos[type])})`
                          : `url(${photoExamples[type]})`,
                      }}
                    ></div>
                    <p className="text-center text-sm font-medium text-[#00191A] mt-2">
                      {label}
                    </p>
                  </label>
                </div>
              ))}
            </div>

            {/* Upload Photos Button */}
            <div className="flex justify-center">
              <button
                onClick={handlePhotoUploadSubmit}
                disabled={isUploadingImages}
                className={`flex items-center justify-between gap-3 btn-primary ${
                  isUploadingImages
                    ? "bg-[#14C5D1] text-white cursor-not-allowed opacity-75"
                    : "bg-[#14C5D1] text-white"
                }`}
              >
                {isUploadingImages ? (
                  <div className="flex items-center justify-center gap-2">
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                    <span>Uploading...</span>
                  </div>
                ) : (
                  <span>{t("getStarted.uploadPhotos")}</span>
                )}
              </button>
            </div>
          </div>
        </div>

        {/* Background Grid Pattern */}
        <div
          className="fixed inset-0 -z-10 opacity-[0.02]"
          style={{
            backgroundImage: `
              linear-gradient(to right, #000 1px, transparent 1px),
              linear-gradient(to bottom, #000 1px, transparent 1px)
            `,
            backgroundSize: "40px 40px",
          }}
        />
      </div>
    );
  }

  return (
    <div className="min-h-screen  flex flex-col">
      {/* Header */}
      <div className="w-full border-b border-[#0000000D] bg-white">
        <div className="w-full px-4 sm:px-6 lg:px-8 py-6 flex items-center justify-between">
          <Link to="/">
            <div className="flex-1 flex justify-center">
              <img
                src="/assets/getlogo.svg"
                alt="Dent Finder"
                className="h-8 w-auto"
              />
            </div>
          </Link>
          <button
            onClick={handleClose}
            className="flex items-center gap-2 text-[#2B2B2B] font-medium text-[18px]"
          >
            <img src="/assets/close.svg" alt="Close" />
            <span>{t("getStarted.close")}</span>
          </button>
        </div>
      </div>

      {/* Main Content */}
      <div
        className="flex-1 flex flex-col px-4 sm:px-6 lg:px-8 py-12 bg-cover bg-center bg-no-repeat relative bg-[#F8FBFC]"
        style={{
          backgroundImage: "url(/assets/Bg.webp)",
        }}
      >
        {/* Top Section - Question and Options */}
        <div className="flex-1 flex items-start justify-center pt-8">
          <div className="w-full relative z-10">
            {/* Progress Indicator */}
            <div className="flex justify-center mb-8">
              <div className="bg-[#E0FDFF] text-[#06646A] px-4 py-1.5 rounded-full text-sm font-medium">
                {t("getStarted.questionOf", {
                  current: currentQuestion,
                  total: totalQuestions,
                })}
              </div>
            </div>

            {/* Question */}
            <div className="text-center mb-8">
              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold bg-gradient-to-r from-[#000087] to-[#000000] bg-clip-text text-transparent mb-3">
                {currentQuestionData?.question || "Who is this treatment for?"}
              </h2>
              {currentQuestionData?.instruction && (
                <p className="text-sm sm:text-sm text-[#000929] mt-2">
                  {currentQuestionData.instruction}
                </p>
              )}
            </div>

            {/* Consultation Method Selection for Question 5 */}
            {currentQuestion === 5 ? (
              <div className="space-y-4 mb-8 max-w-2xl mx-auto">
                <div className="space-y-4">
                  {/* Online Consultation Option */}
                  <button
                    onClick={() => handleConsultationSelect("online")}
                    className={`w-full p-6 rounded-xl text-left transition-all flex items-start gap-4 ${
                      consultationMethod === "online"
                        ? "bg-[#E0F7F9] border border-[#14C5D1]"
                        : "bg-[#F0F2F5] border border-[#E4E7EC] hover:bg-[#EEEEEE]"
                    }`}
                  >
                    <div className="flex-shrink-0 bg-white size-14 rounded-[8px] flex items-center justify-center">
                      <img
                        src="/assets/laptop.svg"
                        alt="Online Consultation"
                        className="w-6 h-6"
                      />
                    </div>
                    <div className="flex-1">
                      <h3 className="text-base sm:text-lg font-semibold text-[#111827] mb-2">
                        {t("getStarted.question5Option1")}
                      </h3>
                      <p className="text-sm text-[#5A5A59]">
                        {t("getStarted.question5Option1Description")}
                      </p>
                    </div>
                    <div className="flex-shrink-0">
                      <div
                        className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                          consultationMethod === "online"
                            ? "border-[#14C5D1] bg-[#14C5D1]"
                            : "border-[#D1D5DB] bg-transparent"
                        }`}
                      >
                        {consultationMethod === "online" && (
                          <div className="w-2 h-2 rounded-full bg-white"></div>
                        )}
                      </div>
                    </div>
                  </button>

                  {/* In-Person Consultation Option */}
                  <button
                    onClick={() => handleConsultationSelect("in-person")}
                    className={`w-full p-6 rounded-xl text-left transition-all flex items-start gap-4 ${
                      consultationMethod === "in-person"
                        ? "bg-[#E0F7F9] border border-[#14C5D1]"
                        : "bg-[#F0F2F5] border border-[#E4E7EC] hover:bg-[#EEEEEE]"
                    }`}
                  >
                    <div className="flex-shrink-0 bg-white size-14 rounded-[8px] flex items-center justify-center">
                      <img
                        src="/assets/user.svg"
                        alt="In-Person Consultation"
                        className="w-6 h-6"
                      />
                    </div>
                    <div className="flex-1">
                      <h3 className="text-base sm:text-lg font-semibold text-[#111827] mb-2">
                        {t("getStarted.question5Option2")}
                      </h3>
                      <p className="text-sm text-[#5A5A59]">
                        {t("getStarted.question5Option2Description")}
                      </p>
                    </div>
                    <div className="flex-shrink-0">
                      <div
                        className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                          consultationMethod === "in-person"
                            ? "border-[#14C5D1] bg-[#14C5D1]"
                            : "border-[#D1D5DB] bg-transparent"
                        }`}
                      >
                        {consultationMethod === "in-person" && (
                          <div className="w-2 h-2 rounded-full bg-white"></div>
                        )}
                      </div>
                    </div>
                  </button>
                </div>

                {/* Note Section */}
                <div className="mt-4">
                  <p className="text-sm text-[#5A5A59]">
                    {(() => {
                      const noteText = t("getStarted.question5Note");
                      const colonIndex = noteText.indexOf(":");
                      if (colonIndex !== -1) {
                        return (
                          <>
                            <span className="font-bold">
                              {noteText.substring(0, colonIndex + 1)}
                            </span>
                            {noteText.substring(colonIndex + 1)}
                          </>
                        );
                      }
                      return noteText;
                    })()}
                  </p>
                </div>
              </div>
            ) : currentQuestion === 6 ? (
              /* Form Fields for Question 6 */
              <div className="space-y-4 mb-8 max-w-2xl mx-auto">
                {/* First Name and Last Name */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <Input
                    label={t("getStarted.firstName")}
                    type="text"
                    value={formData.firstName}
                    onChange={(e) =>
                      handleFormChange("firstName", e.target.value)
                    }
                    placeholder={t("getStarted.enterName")}
                  />
                  <Input
                    label={t("getStarted.lastName")}
                    type="text"
                    value={formData.lastName}
                    onChange={(e) =>
                      handleFormChange("lastName", e.target.value)
                    }
                    placeholder={t("getStarted.enterName")}
                  />
                </div>

                {/* Email */}
                <Input
                  label={t("getStarted.emailAddress")}
                  type="email"
                  value={formData.email}
                  onChange={(e) => handleFormChange("email", e.target.value)}
                  placeholder={t("getStarted.enterEmail")}
                />

                {/* Phone */}
                <PhoneInput
                  countryCode={formData.phoneCountryCode}
                  onCountryCodeChange={(e) =>
                    handleFormChange("phoneCountryCode", e.target.value)
                  }
                  phoneNumber={formData.phoneNumber}
                  onPhoneNumberChange={(e) =>
                    handleFormChange("phoneNumber", e.target.value)
                  }
                />

                {/* Terms and Conditions Checkbox */}
                <div className="pt-4">
                  <label className="flex items-start gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formData.agreedToTerms}
                      onChange={(e) =>
                        handleFormChange("agreedToTerms", e.target.checked)
                      }
                      className="mt-1 w-5 h-5 rounded border-[#E1E1E1] text-[#14C5D1] focus:ring-[#14C5D1] cursor-pointer"
                    />
                    <span className="text-[12px] text-[#00191A]">
                      {t("getStarted.termsText1")}
                      <a
                        href="/#"
                        className="text-[#007B83] underline hover:opacity-80"
                      >
                        {t("getStarted.termsLink1")}
                      </a>
                      {t("getStarted.termsText2")}
                      <a
                        href="/#"
                        className="text-[#007B83] underline hover:opacity-80"
                      >
                        {t("getStarted.termsLink2")}
                      </a>
                      {t("getStarted.termsText3")}
                    </span>
                  </label>
                </div>
              </div>
            ) : (
              /* Options */
              <div className="space-y-4 mb-8 max-w-xl mx-auto">
                {(
                  currentQuestionData?.options || [
                    "I am looking for myself",
                    "I am looking for someone else",
                  ]
                ).map((option, index) => (
                  <button
                    key={index}
                    onClick={() => handleOptionSelect(option)}
                    className={`w-full p-6 rounded-xl text-left transition-all ${
                      selectedOptions.includes(option)
                        ? "bg-[#E0F7F9] border border-[#14C5D1]"
                        : "bg-[#F0F2F5] border border-transparent hover:bg-[#EEEEEE]"
                    }`}
                  >
                    <span className="text-base sm:text-lg font-semibold text-[#111827]">
                      {option}
                    </span>
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Bottom Section - Continue/Send Enquiry and Back Buttons */}
        <div className="flex flex-col items-center pb-8 gap-4 mt-8">
          <div>
            <button
              onClick={handleContinue}
              disabled={!isFormValid()}
              className={`flex items-center justify-between gap-3 btn-primary ${
                isFormValid()
                  ? "bg-[#14C5D1] text-white"
                  : "bg-[#14C5D1] text-white cursor-not-allowed"
              }`}
            >
              {isContactLoading ? (
                <div className="flex items-center justify-center gap-2">
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                  <span>Submitting</span>
                </div>
              ) : (
                <>
                  <span>
                    {currentQuestion === 6
                      ? t("getStarted.sendEnquiry")
                      : t("getStarted.continue")}
                  </span>
                  <img src="/assets/arrow-right.svg" alt="Arrow right" />
                </>
              )}
            </button>
          </div>
          {currentQuestion > 1 && (
            <button
              onClick={handleBack}
              className="text-[#007B83] font-medium text-sm hover:opacity-80 transition-opacity"
            >
              {t("getStarted.back")}
            </button>
          )}
        </div>
      </div>

      {/* Background Grid Pattern */}
      <div
        className="fixed inset-0 -z-10 opacity-[0.02]"
        style={{
          backgroundImage: `
            linear-gradient(to right, #000 1px, transparent 1px),
            linear-gradient(to bottom, #000 1px, transparent 1px)
          `,
          backgroundSize: "40px 40px",
        }}
      />
    </div>
  );
};

export default GetStarted;
